import DraggableModal from '../../generic/DraggableModal'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import InputAdornment from '@mui/material/InputAdornment'
import Grid from '@mui/material/Unstable_Grid2'
import StyledButton from '../../generic/StyledButton'
import PermissionGate from '../../../helpers/PermissionGate'
import { useReducer, useState } from 'react'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { backendPatchWithoutRefresh } from '../../../helpers/callBackend'
import { errorMessage } from '../../../helpers/errorMessageFunc'
import { message } from 'antd'

const reducer = (state, action) => {
  let newState = { ...state }

  newState[action.field] = action.value

  return newState
}

const BalanceOverwriteModal = (props) => {
  const [boxChecked, setBoxChecked] = useState(false)
  const [state, dispatch] = useReducer(reducer, {
    currentBalance: props.accountDetails.current_balance,
    availableBalance: props.accountDetails.available_balance,
    dateTime: new Date(),
  })
  const textFieldProps = {
    type: 'number',
    size: 'small',
    variant: 'outlined',
    sx: {
      width: '50%',
    },
    InputProps: {
      startAdornment: <InputAdornment position='start'>$</InputAdornment>,
      disableUnderline: true,
      sx: {
        borderRadius: 0,
      },
    },
  }

  let userTimeZone = 'America/New_York'
  try {
    userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (error) {
    userTimeZone = 'America/New_York'
  }

  const subtitleProps = {
    sx: {
      fontStyle: 'italic',
    },
    color: 'secondary',
  }

  const handleDateTimeChange = (newDateTime) => {
    const formattedDateTime = newDateTime.format('YYYY-MM-DD HH:mm:ss')
    dispatch({ field: 'dateTime', value: formattedDateTime })
  }

  const getReadableLastFetch = () => {
    const lastFetch = props.accountDetails.last_available_balance_fetch

    let [date, time] = lastFetch.split(' ')
    let [clock] = time.split('.')
    let [hour] = clock.split('-')

    return `${date} ${hour}`
  }

  const handleSubmit = async () => {
    let currentBalance = state.currentBalance
    if (currentBalance.startsWith('$')) {
      currentBalance = currentBalance.slice(1)
    }
    let availableBalance = state.availableBalance
    if (availableBalance.startsWith('$')) {
      availableBalance = availableBalance.slice(1)
    }

    const data = {
      bank_account_id: props.bankAccountId,
      new_current_balance: currentBalance,
      new_available_balance: availableBalance,
      new_timestamp: state.dateTime,
      user_timezone: userTimeZone,
    }

    const newBankAccountResp = await backendPatchWithoutRefresh('/api/bank-account/balance-overwrite/', data)

    if (newBankAccountResp.status != 200) {
      errorMessage('Something went wrong when overwriting your bank account balance.')
      return
    }

    if (newBankAccountResp.data?.data) {
      props.setBankAccountData(newBankAccountResp.data.data)
      props.handleCancelModal(false)
      message.success('Updated account balance information.')
    }
  }

  return (
    <DraggableModal
      width='auto'
      modalVisible={props.visible}
      handleCancelModal={props.handleCancelModal}
      modalTitle={`Balance overwrite for ${props.accountDetails.bank_account_name}`}
      content={
        <Grid container spacing={2} direction='column'>
          <Grid item>
            <Typography>
              If you would like to manually adjust the following fields from our latest feeds, please fill out the
              details below.
            </Typography>
          </Grid>

          <Grid item container alignItems='center' spacing={1}>
            <Grid item xs={3}>
              <Typography>Current Balance</Typography>
            </Grid>
            <Grid item xs={9}>
              <Grid container direction='column'>
                <Grid item>
                  <TextField
                    {...textFieldProps}
                    defaultValue={props.accountDetails.current_balance_raw}
                    onChange={(event) => {
                      dispatch({ field: 'currentBalance', value: event.target.value })
                    }}
                  />
                </Grid>
                <Grid item>
                  <Typography
                    {...subtitleProps}
                  >{`Last reading current balance: ${props.accountDetails.current_balance}`}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item container spacing={2}>
            <Grid item xs={3}>
              <Typography>Available Balance</Typography>
            </Grid>
            <Grid item xs={9}>
              <Grid container direction='column'>
                <Grid item>
                  <TextField
                    {...textFieldProps}
                    defaultValue={props.accountDetails.available_balance_raw}
                    onChange={(event) => {
                      dispatch({ field: 'availableBalance', value: event.target.value })
                    }}
                  />
                </Grid>
                <Grid item>
                  <Typography
                    {...subtitleProps}
                  >{`Last reading available balance: ${props.accountDetails.available_balance}`}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item container spacing={2}>
            <Grid item xs={3}>
              <Typography>Balance Timestamp</Typography>
            </Grid>
            <Grid item xs={9}>
              <Grid container direction='column'>
                <Grid item>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                      onChange={handleDateTimeChange}
                      sx={{
                        width: '50%',
                        '& .MuiInputBase-root': {
                          borderRadius: 0,
                        },
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item>
                  <Typography {...subtitleProps}>{`Last reading timestamp: ${getReadableLastFetch()}`}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Stack direction='row'>
            <Checkbox
              size='medium'
              defaultChecked={boxChecked}
              onChange={(event) => setBoxChecked(event.target.checked)}
            />
            <Typography>
              You acknowledge by clicking submit, you are overwriting the provided data for this account. This will be
              adjusted as more up to date information comes through our feeds for this account, or when you provide
              another manual overwrite.
            </Typography>
          </Stack>
        </Grid>
      }
      actions={
        <PermissionGate
          condition={boxChecked}
          toolTipText={`Please achknowledge that you are overwriting the balance information for ${props.accountDetails.bank_account_name}`}
          spanSettings={{ style: { width: '100%' } }}
          readOnly={<StyledButton customTheme='outlined' disabled label='Submit' />}
        >
          <StyledButton label='Submit' fontSize={14} onClick={handleSubmit} />
        </PermissionGate>
      }
    />
  )
}

export default BalanceOverwriteModal
